import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateCustomerAddressRequest,
  DataAddress,
  UpdateCustomerAddressRequest,
} from './CustomerAddresses.types';

import {
  listCustomerAddresss,
  createCustomerAddress,
  updateCustomerAddress,
  deleteCustomerAddress,
  getCustomerMainAddress,
} from './CustomerAddresses.service';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import Authorities from '@/types/Authority';
import { queryClient } from '@/Root';
import notificationError from '@/utils/functions/notificationError';
import { hasPermissions } from '@/utils/functions/hasPermissions';

export function useCustomerAddresses(customerId?: number) {
  const cache = queryClient.getQueryData([
    'customers',
    customerId,
    'addresses',
  ]);

  return useQuery(
    ['customers', customerId, 'addresses'],
    () => {
      return listCustomerAddresss(customerId);
    },
    {
      enabled:
        !!customerId &&
        hasPermissions([Authorities.REGISTER_CUSTOMER_VIEW]) &&
        !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCustomerAddressesMutation() {
  return useMutation(
    async (customerId: number) => {
      const cache = queryClient.getQueryData<DataAddress>([
        'customers',
        customerId,
        'addresses',
      ]);

      if (cache?.data.some((addres) => addres.customerId === customerId))
        return cache;

      return listCustomerAddresss(customerId);
    },
    {
      onSuccess(data, vars) {
        queryClient.setQueryData(['customers', vars, 'addresses'], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCreateCustomerAddress(customerId: number) {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: CreateCustomerAddressRequest) =>
      createCustomerAddress(customerId, obj),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customers.address.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'customers.address.create.notification.successDescription',
          }),
        });

        await queryClient.removeQueries(['customers', customerId, 'addresses']);
        await queryClient.invalidateQueries([
          'customers',
          customerId,
          'addresses',
        ]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateCustomerAddress(customerId?: number) {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: UpdateCustomerAddressRequest) =>
      updateCustomerAddress(obj, customerId),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customers.address.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'customers.address.update.notification.successDescription',
          }),
        });

        await queryClient.invalidateQueries([
          'customers',
          customerId,
          'addresses',
        ]);

        queryClient.removeQueries(['customers', customerId, 'addresses']);
        queryClient.removeQueries(['customer/detail/general', customerId]);
        queryClient.removeQueries(['customer/detail/reduced', customerId]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteCustomerAddress(customerId: number) {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation(
    (id: number) => deleteCustomerAddress(customerId, id),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customers.address.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'customers.address.remove.notification.successDescription',
          }),
        });

        await queryClient.removeQueries(['customers', customerId, 'addresses']);
        await queryClient.invalidateQueries([
          'customers',
          customerId,
          'addresses',
        ]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useGetCustomerMainAddress() {
  return useMutation(getCustomerMainAddress);
}
